// v2 squirrel — a proper sit-up pose: haunches planted on the ground, an upright
// torso with a pale belly, tiny paws held to the chest, a round head with tufted
// ears, and a tall S-curved plume tail that rises behind and curls back over the
// head. Two coats: red and grey. Ported 1:1 from age-of-mcp's v2/squirrel.ts.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

const COATS = [
  { coat: "#a85a28", head: "#b06430", belly: "#e8d8be", ear: "#7e451e", tail: "#b87038", tip: "#c8884a" },
  { coat: "#8a8a92", head: "#94949c", belly: "#e8e6e0", ear: "#6e6e76", tail: "#9a9aa2", tip: "#b0b0b8" },
];

function squirrelParts(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = [
    // haunches planted on the ground
    { geometry: new THREE.IcosahedronGeometry(0.02, 0), color: c.coat,
      position: [0.004, 0.02, 0], scale: [1.25, 1.0, 1.2] },
    // upright torso with a pale belly front
    { geometry: new THREE.IcosahedronGeometry(0.016, 0), color: c.coat,
      position: [0.012, 0.044, 0], scale: [1.0, 1.3, 0.95] },
    { geometry: new THREE.IcosahedronGeometry(0.0105, 0), color: c.belly,
      position: [0.022, 0.042, 0], scale: [0.8, 1.15, 0.7] },
    // round head with tufted ears and a small muzzle
    { geometry: new THREE.IcosahedronGeometry(0.0145, 0), color: c.head,
      position: [0.021, 0.069, 0] },
    { geometry: new THREE.ConeGeometry(0.0045, 0.013, 4), color: c.ear,
      position: [0.0155, 0.082, 0.0085] },
    { geometry: new THREE.ConeGeometry(0.0045, 0.013, 4), color: c.ear,
      position: [0.0155, 0.082, -0.0085] },
    { geometry: new THREE.IcosahedronGeometry(0.006, 0), color: c.belly,
      position: [0.033, 0.065, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.0025, 0), color: "#241a12",
      position: [0.0385, 0.066, 0] },
    // tiny paws held to the chest
    { geometry: new THREE.IcosahedronGeometry(0.0045, 0), color: c.head,
      position: [0.027, 0.052, 0.0065] },
    { geometry: new THREE.IcosahedronGeometry(0.0045, 0), color: c.head,
      position: [0.027, 0.052, -0.0065] },
    // long hind feet flat on the ground
    { geometry: new THREE.BoxGeometry(0.02, 0.007, 0.008), color: c.coat,
      position: [0.012, 0.0035, 0.0125] },
    { geometry: new THREE.BoxGeometry(0.02, 0.007, 0.008), color: c.coat,
      position: [0.012, 0.0035, -0.0125] },
    // the plume — an S-curve rising behind and curling over the head
    { geometry: new THREE.IcosahedronGeometry(0.0115, 0), color: c.tail,
      position: [-0.013, 0.012, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.016, 0), color: c.tail,
      position: [-0.027, 0.04, 0], scale: [1.0, 1.35, 0.85] },
    { geometry: new THREE.IcosahedronGeometry(0.017, 0), color: c.tail,
      position: [-0.024, 0.076, 0], scale: [1.0, 1.35, 0.85] },
    { geometry: new THREE.IcosahedronGeometry(0.013, 0), color: c.tip,
      position: [-0.007, 0.102, 0], scale: [1.1, 1.2, 0.85] },
  ];
  return parts;
}

export const squirrelV2 = defineModel({
  id: "squirrel-v2",
  batchId: "fable",
  name: "Squirrel (v2)",
  tags: ["creature","squirrel","low-poly","age-of-mcp","fable"],
  summary: "Low-poly squirrel.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.06,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => squirrelParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => squirrelParts(1) },
  ],
  animations: [],
});
